"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import { useRef } from "react"
import "swiper/css"
import "swiper/css/navigation"
import { Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"

interface GalleryImage {
  src: string
  alt: string
}

const Wrapper = styled.section`
  width: 100%;
  margin: clamp(40px, 6vw, 45px) auto;
  position: relative;
  direction: rtl;
  padding: 0;
  box-sizing: border-box;
  direction: ltr;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin: 15px 0;
  }
`

const Heading = styled.h2`
  font-weight: 800;
  margin: 0 0 20px;
  line-height: 1.05;
  font-size: clamp(32px, 5vw, 72px);
  text-align: center;
  color: #1988e2;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-bottom: 0px;
  }
`

const SliderShell = styled.div`
  position: relative;
  width: 100%;
  .swiper {
    overflow: hidden;
    padding-bottom: 32px; /* room for focus ring if needed */
    direction: ltr;
    ${(p) => p.theme.breakpoints.down("sm")} {
      padding: 20px 0;
    }
  }
`

const SlideInner = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 644 / 355;
  border-radius: 0;
  overflow: hidden;
  background: #e5eef7;
  ${(p) => p.theme.breakpoints.down("sm")} {
    aspect-ratio: 360 / 250;
  }
`

const StyledNavButton = styled.button<{ $direction: "prev" | "next" }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(p) => (p.$direction === "prev" ? "left: 5vw;" : "right: 5vw;")}
  z-index: 10;
  width: clamp(40px, 3vw, 64px);
  height: clamp(40px, 3vw, 64px);
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  color: #1988e2;
  cursor: pointer;
  font-size: clamp(18px, 2vw, 28px);
  font-weight: 600;
  transition: background 0.2s, color 0.2s, transform 0.2s;
  &:hover {
    background: #1988e2;
    color: #fff;
  }
  &:active {
    transform: translateY(calc(-50% + 2px));
  }
  &:focus-visible {
    outline: 3px solid #ffe600;
    outline-offset: 2px;
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 27px;
    height: 27px;
  }
`

const StyledBanner = styled.div`
  background-color: #1988e2;
  color: white;
  position: absolute;
  bottom: 20px;
  padding: 7px 17px;
  font-size: 30px;
  line-height: 1;
  font-weight: 600;
  inset-inline-start: 0;
  z-index: 1;
  span {
    color: #ffe600;
  }
  &:after {
    content: "";
    position: absolute;
    height: 100%;
    inset-inline-start: calc(100% - 2px); /* extend to the left */
    width: 30px;
    top: 0;
    background-color: #1988e2;
    clip-path: polygon(0 0, 100% 0, 0 100%);
    transform: scaleX(-1);
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    font-size: 20px;
    padding: 5px 15px;
    &:after {
      content: "";
      width: 20px;
    }
  }
`

// Internal static images (filenames include spaces / Hebrew; browsers will encode automatically)
const images: GalleryImage[] = [
  {
    src: "/landingpages/homeupgrades/lower-gallery/Cam02 Park_tower_ramat_gan_web 1.jpg",
    // eslint-disable-next-line quotes
    alt: 'PARK TOWER, ר"ג',
  },
  {
    src: "/landingpages/homeupgrades/lower-gallery/hashaked_kiryat_onu_erev 2.jpg",
    alt: "השקד אונו, קריית אונו",
  },
  // {
  //   src: "/landingpages/homeupgrades/lower-gallery/אבן-שפרוט-הרצליה 1.jpg",
  //   alt: "אבן שפרוט, הרצליה",
  // },
  {
    src: "/landingpages/homeupgrades/lower-gallery/BIRD VIEW_Mea_Raanana_web 1.jpg",
    alt: "מתחם המאה, רעננה",
  },

  {
    src: "/landingpages/homeupgrades/lower-gallery/Street-view-Borochov-24_web_wide 1.jpg",
    alt: "B&H, רעננה",
  },
  {
    src: "/landingpages/homeupgrades/lower-gallery/netaniya_wide 1.jpg",
    alt: "גלי נתניה, נתניה",
  },
  {
    src: "/landingpages/homeupgrades/lower-gallery/cam04_day_1_web 1.jpg",
    alt: `אנכי, נאות אפקה ת"א`,
  },
  {
    src: "/landingpages/homeupgrades/lower-gallery/004_web 1.jpg",
    alt: "הדר, גבעת שמואל",
  },
  {
    src: "/landingpages/homeupgrades/lower-gallery/remez36-c02_0-8k-300dpi_web 1.jpg",
    alt: `רמז 36, ת"א`,
  },
  {
    src: "/landingpages/homeupgrades/lower-gallery/A neot afeka by ashdar_web 1.jpg",
    alt: `מגדל נאות אפקה, ת"א`,
  },
  {
    src: "/landingpages/homeupgrades/lower-gallery/הדמיה_צירלסון 1.jpg",
    alt: `צירלסון COMPLEX, ת"א`,
  },
  {
    src: "/landingpages/homeupgrades/lower-gallery/CAM_01D_6K-gigapixel-lines-scale-5_33x 1.jpg",
    alt: `זכרון יעקב 7, ת"א`,
  },
  // {
  //   src: "/landingpages/homeupgrades/lower-gallery/unu-top-night-xl_hapardes_SG 1.jpg",
  //   alt: `הפרדס, קרית אונו`,
  // },
]

export default function LowerGallery({ className }: { className?: string }) {
  const prevRef = useRef<HTMLButtonElement | null>(null)
  const nextRef = useRef<HTMLButtonElement | null>(null)

  return (
    <Wrapper className={className} aria-labelledby="lower-gallery-heading">
      <Heading id="lower-gallery-heading">
        זה הבית שלכם,
        <br />
        בדיוק כמו שדמיינתם.
      </Heading>
      <SliderShell>
        <StyledNavButton
          ref={prevRef}
          type="button"
          aria-label="הקודם"
          $direction="prev"
          className="lower-gallery-prev"
        >
          {"<"}
        </StyledNavButton>
        <StyledNavButton
          ref={nextRef}
          type="button"
          aria-label="הבא"
          $direction="next"
          className="lower-gallery-next"
        >
          {">"}
        </StyledNavButton>
        <Swiper
          modules={[Navigation]}
          spaceBetween={24}
          slidesPerView={1}
          navigation={{
            prevEl: ".lower-gallery-prev",
            nextEl: ".lower-gallery-next",
          }}
          breakpoints={{
            768: {
              // tablet / small desktop
              slidesPerView: 2.2,
              spaceBetween: 13,
            },
            1100: {
              // full desktop
              slidesPerView: 3.2,
              spaceBetween: 13,
            },
          }}
        >
          {images.map((img) => (
            <SwiperSlide key={img.src} aria-label={img.alt} role="group">
              <SlideInner>
                <StyledBanner>
                  {img.alt} <span>{">>"}</span>
                </StyledBanner>
                <Image src={img.src} alt={img.alt} fill sizes="(max-width: 768px) 90vw, 30vw" />
              </SlideInner>
            </SwiperSlide>
          ))}
        </Swiper>
      </SliderShell>
    </Wrapper>
  )
}
